import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_VENDORS_EXCLUSIONS_LISTS } from '../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzEnumFilterWebComponent } from '../../../../../../../../../ui/gzEnumFilter';
import { EnumListItem } from '../../../../../../../../../ui/gzEnumList/types';
import { toggleVisibility } from '../../../../../../../../../utils/htmlElement.utils';
import { InputWidthValues } from '../../../../../../../../../ui/gzInput';
import { getTableColumns } from './grid-config';
import { getDataTableDefaultStrings } from '../../../../../../../../../ui/gzDataTable/types/strings';
import { GzDataTableWebComponent } from '../../../../../../../../../ui/gzDataTable';
import { FormFieldInput } from '../../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { AntimalwareVendorsListResponse } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_VENDORS_EXCLUSIONS_LISTS)
export class GzAntimalwareVendorsExclusionsListsWebComponent extends FormFieldInput<string[]> {
  /**
   * Reference to the selected exclusion list ids.
   */
  private selectedExclusionListIdsRef: string[] = [];
  /**
   * Available lists of exclusions.
   */
  private exclusionsLists: AntimalwareVendorsListResponse[] = [];

  @query('#rule-sets-grid')
  private gridComponent: GzDataTableWebComponent<AntimalwareVendorsListResponse>;

  @query('#exclusion-list-selector')
  private exclusionListSelector: GzEnumFilterWebComponent;

  /**
   * Styles for the component.
   */
  static get styles() {
    return styles;
  }

  /**
   * Set form value of the component (ids of the selected items).
   * @param value
   */
  public setFormValue(value: string[]): void {
    this.selectedExclusionListIdsRef = value;
    this.computeViewData();
  }

  /**
   * Set data of the component (available lists).
   * @param exclusionsLists
   */
  set lists(exclusionsLists: AntimalwareVendorsListResponse[]) {
    this.exclusionsLists = exclusionsLists;
    this.computeViewData();
  }

  /**
   * Get value of the component (ids of the selected items).
   */
  public getFormValue(): string[] {
    return this.selectedExclusionListIdsRef;
  }

  /**
   * Render specific content.
   */
  protected render() {
    // no need to render if data is missing
    if (this.exclusionsLists == null) {
      return html``;
    }

    // wait for the component to be updated before building the grid and the selector
    this.updateComplete.then(() => {
      this.computeViewData();
    });

    return html`
      <div class="exclusion-list-form--row">
        <gz-enum-filter
          class="exclusion-list-form--field"
          id="exclusion-list-selector"
          labelWidth=${InputWidthValues.XXLARGE}
          placeholder="${translateFn('Select vendor or product')}"
          searchPlaceholderString="${translateFn('Search')}"
          confirmActionButtonString="${translateFn('Apply')}"
          noSearchResultsString="${translateFn('No exclusion list found.')}"
          allItemsLabel="${translateFn('All exclusion lists')}"
        ></gz-enum-filter>
        <gz-icon-button
          class="exclusion-list-form--action"
          id="addButton"
          size="32"
          fontSize="20"
          buttonType="square"
          icon="ds-icon-add"
          buttonFill="primary"
          @click="${this.onAddGridItem}"
        >
        </gz-icon-button>
      </div>

      <gz-data-table
        id="rule-sets-grid"
        .columns="${getTableColumns()}"
        .strings="${{
          ...getDataTableDefaultStrings(),
          deleteRowTooltip: translateFn('Delete'),
        }}"
        allowRowDeletion
        @row-deleted="${this.onDeleteGridItem.bind(this)}"
      >
      </gz-data-table>
    `;
  }

  /**
   * Remove item from grid and update filter options.
   * @param event
   */
  protected onDeleteGridItem(event: CustomEvent) {
    const itemId = event.detail.item._id;

    if (!itemId) {
      return;
    }

    const idxRef = this.selectedExclusionListIdsRef.findIndex(ruleSetId => ruleSetId === itemId);
    this.selectedExclusionListIdsRef.splice(idxRef, 1);

    this.computeViewData();
  }

  /**
   * Add item to the grid and update filter options.
   * @param event
   */
  private onAddGridItem(event: CustomEvent): void {
    const selectedRuleSetIds = this.exclusionListSelector.filterOptions
      .filter(option => option.selected)
      .map(option => option.value as string);

    this.selectedExclusionListIdsRef.push(...selectedRuleSetIds);
    this.computeViewData();
  }

  /**
   * Compute view data.
   * @private
   */
  private computeViewData() {
    this.computeFilterOptions();
    this.computeGridValues();
  }

  /**
   * Update filter options.
   */
  private computeFilterOptions() {
    this.exclusionListSelector.filterOptions = this.exclusionsLists.map(ruleSet => {
      const isSelectedAndDisabled = this.selectedExclusionListIdsRef.includes(ruleSet._id);
      return {
        label: ruleSet.name,
        value: ruleSet._id,
        selected: false,
        disabled: isSelectedAndDisabled,
      } as EnumListItem;
    });
  }

  /**
   * Update grid values.
   */
  private computeGridValues() {
    this.gridComponent.setFormValue(
      this.exclusionsLists.filter(ruleSet => this.selectedExclusionListIdsRef.includes(ruleSet._id))
    );

    this.toggleGridVisibility();
  }

  /**
   * Toggle grid visibility.
   */
  private toggleGridVisibility(): void {
    if (!this.gridComponent) {
      return;
    }

    toggleVisibility(this.gridComponent, !!this.gridComponent.getFormValue().length);
  }
}
